import { create } from 'zustand';
import { MODES } from '@scio3/shared-types';

/**
 * Navigation mode for the screen list — a location, not a filter:
 * DEFAULT (normal browse), SHARE_WITH_ME (folders shared with me), or FAVORITES
 * (the current user's favorited screens + folders, root-scoped). Mirrors the assets list.
 */
type ScreenListMode = typeof MODES.DEFAULT | typeof MODES.SHARE_WITH_ME | typeof MODES.FAVORITES;

interface ScreenFilterState {
  status: number[];
  orientation: string[];
  contentType: string[];
  tags: string[];
  searchText: string;
  searchScope: 'all' | 'folder';
  isFilterPanelOpen: boolean;
  /** Navigation mode: SHARE_WITH_ME / FAVORITES are locations, not filters. */
  mode: ScreenListMode;

  setStatus: (status: number[]) => void;
  setOrientation: (orientation: string[]) => void;
  setContentType: (contentType: string[]) => void;
  setTags: (tags: string[]) => void;
  setSearchText: (text: string) => void;
  setSearchScope: (scope: 'all' | 'folder') => void;
  toggleFilterPanel: () => void;
  setMode: (mode: ScreenListMode) => void;
  clearFilters: () => void;
  hasActiveFilters: () => boolean;
}

export const useScreenFilterStore = create<ScreenFilterState>((set, get) => ({
  status: [],
  orientation: [],
  contentType: [],
  tags: [],
  searchText: '',
  searchScope: 'all',
  isFilterPanelOpen: false,
  mode: MODES.DEFAULT,

  setStatus: (status) => set({ status }),
  setOrientation: (orientation) => set({ orientation }),
  setContentType: (contentType) => set({ contentType }),
  setTags: (tags) => set({ tags }),
  setSearchText: (text) => set({ searchText: text }),
  setSearchScope: (scope) => set({ searchScope: scope }),
  toggleFilterPanel: () => set((s) => ({ isFilterPanelOpen: !s.isFilterPanelOpen })),
  setMode: (mode) =>
    set({
      mode,
      status: [],
      orientation: [],
      contentType: [],
      tags: [],
      searchText: '',
    }),
  clearFilters: () =>
    set({
      status: [],
      orientation: [],
      contentType: [],
      tags: [],
      searchText: '',
      mode: MODES.DEFAULT,
    }),
  hasActiveFilters: () => {
    const s = get();
    return (
      s.status.length > 0 ||
      s.orientation.length > 0 ||
      s.contentType.length > 0 ||
      s.tags.length > 0 ||
      s.searchText.length > 0 ||
      s.mode === MODES.SHARE_WITH_ME ||
      s.mode === MODES.FAVORITES
    );
  },
}));
